import type { BackendAPITypes } from '../../../generated/blueprint.ts';
import type { ValidationTag } from '../../../generated/enums.ts';
import { decimalToLocalizedString } from '../../../lib/Money.ts';
import { getCurrencySymbol } from '../../../lib/utils.ts';

export type ValidationResult =
    BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerClientDocumentValidateDocumentFieldsPostResponseBody;
export type ValidationIssue = ValidationResult['issues'][number];

export const LINE_ITEMS_SECTION_SCROLL_FIELD = 'line_items_section';

type IssueMessage = string | ((params: Record<string, string>) => string);

function withExpectedAmount(fallback: string, withAmount: (amount: string) => string, amount: string | undefined, currency: string | undefined): string {
    const decimal = amount === undefined || amount.trim() === '' ? Number.NaN : Number(amount);
    if (!Number.isFinite(decimal) || currency === undefined || currency.trim() === '') {
        return fallback;
    }
    return withAmount(`${getCurrencySymbol(currency)} ${decimalToLocalizedString(decimal)}`);
}

const ISSUE_MESSAGES: Record<ValidationTag, IssueMessage> = {
    category_set: 'Categorie is verplicht',
    category_not_billable: 'Deze categorie kan niet als factuur of bonnetje worden opgeslagen',
    direction_set: 'Inkoop/verkoop is verplicht',
    relation_name_present: 'Bedrijfsnaam is verplicht',
    relation_country_present: 'Land van relatie is verplicht',
    vat_number_format: 'Ongeldig BTW-nummer formaat',
    has_date: 'Datum ontbreekt',
    normalized_date_set: 'Datum is verplicht',
    normalized_time_set: 'Tijd is verplicht',
    due_date_not_before_normalized_date: 'Vervaldatum ligt voor de factuurdatum',
    reference_identifier_present: 'Factuurnummer is verplicht',
    total_amount_set: 'Totaalbedrag ontbreekt',
    total_amount_currency_set: 'Valuta ontbreekt',
    total_amount_sign_matches_credit_note: (params: Record<string, string>) =>
        params.expected_sign === 'negative'
            ? 'Bij een creditnota moet het bedrag negatief zijn'
            : 'Bij een factuur moet het bedrag positief zijn',
    vat_amount_below_total: 'BTW is hoger dan het totaalbedrag',
    vat_reverse_charge_present: 'Kies ja of nee',
    vat_reverse_charge_and_vat_exempt_mutually_exclusive:
        'Verlegde btw en btw-vrijstelling kunnen niet beide van toepassing zijn',
    seller_and_buyer_name_not_same: 'Koper en verkoper mogen niet hetzelfde zijn',
    max_tokens_limit: 'We konden niet alle informatie uitlezen',
    line_items_present: 'Regels ontbreken. Voeg minimaal een regel toe.',
    line_items_sum_matches_total_amount_payable: (params: Record<string, string>) =>
        withExpectedAmount('Wijkt af van de regels', (amount) => `Regels tellen op tot ${amount}`, params.expected_total, params.currency),
    line_item_vat_sum_matches_bill_vat_total: (params: Record<string, string>) =>
        withExpectedAmount('Wijkt af van de regels', (amount) => `BTW van de regels is ${amount}`, params.expected_vat, params.currency),
    'line_items.has_name_or_description': 'Omschrijving is verplicht',
    'line_items.name_or_description': 'Omschrijving is verplicht',
    'line_items.net_amount_set': 'Verplicht',
    'line_items.vat_rate_set': 'Verplicht',
    'line_items.discount_non_negative': 'Korting mag niet negatief zijn',
    'line_items.net_not_exceed_total': 'Het nettobedrag mag het totaalbedrag niet overschrijden',
    'line_items.net_plus_vat_equals_total': 'Netto plus BTW komt niet overeen met het regeltotaal',
    'line_items.vat_calc_accuracy': 'Het BTW-bedrag past niet bij het BTW-tarief van de regel',
    'line_items.gla_set': 'Kies een grootboekrekening',
    unknown_check: (params: Record<string, string>) => params.tag ?? 'Onbekende controle',
};

export const VALIDATION_TAGS = Object.keys(ISSUE_MESSAGES) as ValidationTag[];

const RENDERED_LINE_ITEM_FIELDS = new Set(['name_or_description', 'net_amount', 'vat_amount', 'vat_rate_percentage', 'category_id']);

const BACKEND_FIELD_TO_FORM_FIELD: Record<string, string> = {
    vat_rate_percentage: 'vat_rate_percentage_id',
};

const BANNER_ONLY_TAGS = new Set<ValidationTag>([
    'max_tokens_limit',
    'line_items_present',
    'line_items.discount_non_negative',
    'unknown_check',
]);

function toFormFieldName(backendField: string): string {
    return BACKEND_FIELD_TO_FORM_FIELD[backendField] ?? backendField;
}

export function getIssueMessage(issue: ValidationIssue): string {
    const message = ISSUE_MESSAGES[issue.tag];
    if (message === undefined) {
        return issue.tag;
    }
    if (typeof message === 'string') {
        return message;
    }
    const params = Object.fromEntries(
        Object.entries(issue.params ?? {}).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
    );
    return message(params);
}

export function mapIssueToFormFieldName(issue: ValidationIssue, lineItemIds: string[]): string | null {
    const path = issue.location.field_path;
    if (path === 'line_items') {
        return null;
    }

    const match = path.match(/^line_items\.(\d+)\.(.+)$/);
    if (!match) {
        return toFormFieldName(path);
    }

    const itemId = lineItemIds[Number(match[1])];
    const field = match[2];
    if (!itemId || !RENDERED_LINE_ITEM_FIELDS.has(field)) {
        return null;
    }

    return `line_items.${itemId}.${toFormFieldName(field)}`;
}

export interface IssueFieldError {
    name: string;
    errors: string[];
}

export type BannerIssueItem = {
    kind: 'message';
    message: string;
    fieldName: string | null;
};

export type BannerFieldErrorsItem = {
    kind: 'field_errors';
    count: number;
};

export type BannerItem = BannerIssueItem | BannerFieldErrorsItem;

export interface ProcessedValidationIssues {
    fieldErrors: IssueFieldError[];
    lineItemsError: string | null;
    bannerItems: BannerItem[];
}

export function processValidationIssues(
    issues: ValidationIssue[],
    lineItemIds: string[],
    visibleFieldNames?: Set<string>,
): ProcessedValidationIssues {
    const errorsByField = new Map<string, Set<string>>();
    const bannerMessages: BannerIssueItem[] = [];
    let lineItemsError: string | null = null;

    for (const issue of issues) {
        const message = getIssueMessage(issue);
        const fieldName = mapIssueToFormFieldName(issue, lineItemIds);
        const path = issue.location.field_path;
        const fieldIsVisible = fieldName != null && (visibleFieldNames == null || visibleFieldNames.has(fieldName));
        const showOnField = fieldIsVisible && !BANNER_ONLY_TAGS.has(issue.tag);

        if (!lineItemsError && (path === 'line_items' || issue.tag === 'line_items_present')) {
            lineItemsError = message;
            bannerMessages.push({
                kind: 'message',
                message,
                fieldName: LINE_ITEMS_SECTION_SCROLL_FIELD,
            });
            continue;
        }

        if (showOnField && fieldName) {
            const messages = errorsByField.get(fieldName) ?? new Set<string>();
            messages.add(message);
            errorsByField.set(fieldName, messages);
            continue;
        }

        bannerMessages.push({
            kind: 'message',
            message,
            fieldName: fieldIsVisible ? fieldName : null,
        });
    }

    const fieldErrors = [...errorsByField].map(([name, errors]) => ({ name, errors: [...errors] }));
    const bannerItems: BannerItem[] = [...bannerMessages];
    if (fieldErrors.length > 0) {
        bannerItems.push({ kind: 'field_errors', count: fieldErrors.length });
    }

    return {
        fieldErrors,
        lineItemsError,
        bannerItems,
    };
}

export function mapIssuesToFieldErrors(issues: ValidationIssue[], lineItemIds: string[]): IssueFieldError[] {
    return processValidationIssues(issues, lineItemIds).fieldErrors;
}

export function getLineItemsSectionError(issues: ValidationIssue[]): string | null {
    const tableIssue = issues.find(issue =>
        issue.location.field_path === 'line_items' || issue.tag === 'line_items_present',
    );
    return tableIssue ? getIssueMessage(tableIssue) : null;
}
